/**
 * The column guide — an export explains its own columns (owner request 2026-10-01, `docs/adr/9045`).
 *
 *   "On the Why pages, the export file should come with a second file whose sole job is to explain
 *    the header on the first row, so we know what we are reading."
 *
 * One row per column of the export's CSV, in the CSV's order: the header a person reads (as the
 * workbook heads it), the machine name (the CSV's own header, byte for byte), the unit or where the
 * currency is, and what the column MEANS (`ExportColumn.descriptionKey`) — what is measured, its
 * sign or unit, what an empty cell means. A money column's currency column, which the CSV writes
 * beside it, has its own row, so every header of the CSV has exactly one row here.
 *
 * **It reads nothing.** A guide describes columns, not rows: it is built from the column
 * definitions alone, with no request to the service, so it downloads instantly and the same for
 * every run. The workbook's «Colonne» sheet is the same table, written by `xlsx.ts`.
 *
 * The file is a CSV like the export's own — UTF-8 with a BOM, CRLF, RFC 4180 quoting, a text
 * that would start a formula neutralised — named after the export's title with the locale's
 * «colonne» suffix: «Motivi-dei-trasferimenti_colonne.csv».
 */

import { escapeField, neutraliseFormula, UTF8_BOM } from './csv';
import { headerFields } from './table';
import type { ColumnGuideSource, ExportColumn, ExportOutcome, ExportTranslate } from './types';
import { columnMeaning, machineName, meaningOrGap, translated, unitWords } from './xlsx';

/** One row of the guide. */
export interface ColumnGuideRow {
  /** The header a person reads — the workbook's, in the reader's language. */
  readonly header: string;
  /** The CSV's header for the column, exactly. */
  readonly machine: string;
  /** The unit in words, or where an amount's currency is; empty when the column has none. */
  readonly unit: string;
  /** What the column means; «Description not yet written» when the column does not say (`undescribedColumns`). */
  readonly meaning: string;
}

/** The human header, as the workbook writes it: the catalogue's title, the unit in words beside it. */
function humanHeader(column: ExportColumn, translate: ExportTranslate, withUnit = true): string {
  const title = column.header ?? translated(translate, column.headerKey, column.headerValues) ?? column.key;
  const unit = withUnit && column.kind !== 'money' ? unitWords(column.unit, translate) : null;
  return unit === null ? title : `${title} (${unit})`;
}

/** The guide's rows for these columns, in the CSV's order — a money column's currency column included. Pure. */
export function columnGuideRows(columns: readonly ExportColumn[], translate: ExportTranslate): ColumnGuideRow[] {
  return columns.flatMap((column): ColumnGuideRow[] => {
    const row: ColumnGuideRow = {
      header: humanHeader(column, translate),
      machine: machineName(column),
      unit: column.kind === 'money'
        ? translate('export.columnGuide.unit.money', { column: `${column.key}_currency` })
        : (unitWords(column.unit, translate) ?? ''),
      meaning: meaningOrGap(column, translate),
    };
    if (column.kind !== 'money') return [row];
    const currency = translated(translate, 'export.header.currency') ?? 'currency';
    return [row, {
      header: `${humanHeader(column, translate, false)} · ${currency}`,
      machine: `${column.key}_currency`,
      unit: '',
      meaning: translate('export.columnGuide.currencyMeaning', { column: column.key }),
    }];
  });
}

/** The keys of the columns with no meaning in this locale — a guide with a gap is a test failure. Pure. */
export function undescribedColumns(columns: readonly ExportColumn[], translate: ExportTranslate): string[] {
  return columns.filter((column) => columnMeaning(column, translate) === '').map((column) => column.key);
}

/** The guide as CSV text: the BOM, the translated header row, one row per column. Pure. */
export function columnGuideCsv(source: ColumnGuideSource, translate: ExportTranslate): string {
  const header = [
    translate('export.columnGuide.column.header'),
    translate('export.columnGuide.column.machine'),
    translate('export.columnGuide.column.unit'),
    translate('export.columnGuide.column.meaning'),
  ];
  const rows = columnGuideRows(source.columns, translate).map((row) => [row.header, row.machine, row.unit, row.meaning]);
  // The machine names are the CSV's own header, in its order — a guide that disagreed would explain another file.
  const machines = rows.map((row) => row[1]);
  const expected = headerFields(source.columns);
  if (machines.join('\u0000') !== expected.join('\u0000')) {
    throw new Error(`column guide for "${source.id}" does not follow the CSV's header`);
  }
  const line = (fields: readonly string[]): string => fields.map((field) => escapeField(neutraliseFormula(field))).join(',');
  return `${UTF8_BOM}${[line(header), ...rows.map(line)].join('\r\n')}\r\n`;
}

/** Characters no file system accepts in a name, and control characters. */
// eslint-disable-next-line no-control-regex
const UNSAFE_IN_FILENAME = /[\\/:*?"<>|\u0000-\u001f\u007f]+/g;

/** `<Title>_<suffix>.csv`, the title the export's own, translated, the suffix the locale's «colonne». */
export function columnGuideFilename(source: ColumnGuideSource, translate: ExportTranslate): string {
  const part = (value: string): string => value.replace(UNSAFE_IN_FILENAME, ' ').trim().replace(/\s+/g, '-').replace(/^\.+|\.+$/g, '');
  const title = translated(translate, source.titleKey);
  const suffix = translated(translate, 'export.columnGuide.fileSuffix') ?? 'columns';
  return `${part(title ?? source.id) || part(source.id)}_${part(suffix)}.csv`;
}

/**
 * The guide file, ready to save — synchronous, from the columns alone. The download controls call it
 * beside the export's own CSV and Excel and hand the outcome to `saveOutcome`, as every export does.
 */
export function runColumnGuide(source: ColumnGuideSource, options: { readonly translate: ExportTranslate }): ExportOutcome {
  return {
    mode: 'synchronous',
    blob: new Blob([columnGuideCsv(source, options.translate)], { type: 'text/csv;charset=utf-8' }),
    filename: columnGuideFilename(source, options.translate),
  };
}
